import {
  NotificationCountsResponseSchema,
  NotificationListResponseSchema,
  type NotificationType,
} from '@invoice/shared/schemas';
import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { apiFetch } from '../api';

export type NotificationsList = ReturnType<typeof NotificationListResponseSchema.parse>;
export type NotificationCounts = ReturnType<typeof NotificationCountsResponseSchema.parse>;

export interface NotificationFilters {
  customerId?: string | undefined;
  unreadOnly?: boolean | undefined;
  limit?: number | undefined;
  search?: string | undefined;
  type?: NotificationType | undefined;
}

export function notificationsKey(filters: NotificationFilters = {}) {
  return [
    'notifications',
    filters.customerId ?? '',
    filters.unreadOnly ? 'unread' : 'all',
    filters.limit ?? 100,
    filters.search?.trim() ?? '',
    filters.type ?? '',
  ] as const;
}

export function notificationCountsKey() {
  return ['notification-counts'] as const;
}

export async function fetchNotifications(
  filters: NotificationFilters = {},
  signal?: AbortSignal,
): Promise<NotificationsList> {
  return apiFetch('/notifications', {
    schema: NotificationListResponseSchema,
    ...(signal ? { signal } : {}),
    searchParams: {
      ...(filters.customerId ? { customerId: filters.customerId } : {}),
      ...(filters.unreadOnly ? { unreadOnly: true } : {}),
      ...(typeof filters.limit === 'number' ? { limit: filters.limit } : {}),
      ...(filters.search?.trim() ? { search: filters.search.trim() } : {}),
      ...(filters.type ? { type: filters.type } : {}),
    },
  });
}

export async function fetchNotificationCounts(signal?: AbortSignal): Promise<NotificationCounts> {
  return apiFetch('/notifications/counts', {
    schema: NotificationCountsResponseSchema,
    ...(signal ? { signal } : {}),
  });
}

export function useNotifications(
  filters: NotificationFilters = {},
  options: { enabled?: boolean } = {},
): UseQueryResult<NotificationsList, Error> {
  return useQuery({
    queryKey: notificationsKey(filters),
    queryFn: ({ signal }) => fetchNotifications(filters, signal),
    staleTime: 60_000,
    enabled: options.enabled ?? true,
  });
}

export function useNotificationCounts(): UseQueryResult<NotificationCounts, Error> {
  return useQuery({
    queryKey: notificationCountsKey(),
    queryFn: ({ signal }) => fetchNotificationCounts(signal),
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useClearNotifications(): UseMutationResult<void, Error, void> {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: async () => {
      await apiFetch('/notifications', { method: 'DELETE' });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: notificationCountsKey() });
    },
  });
}

export function useDeleteNotification(): UseMutationResult<void, Error, { id: string }> {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: string }>({
    mutationFn: async ({ id }) => {
      await apiFetch(`/notifications/${id}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: notificationCountsKey() });
    },
  });
}
